/**
 * Product Comparison Table - Storefront API Hydration
 *
 * Fetches competitor metaobject data via Shopify Storefront API and populates
 * the comparison table with resolved field values. Server renders structural data,
 * this script hydrates GID-linked metaobject references client-side.
 *
 * Requires: Storefront API public access token with 'unauthenticated_read_metaobjects' scope
 *
 * Customizer behaviour:
 *   - When window.Shopify.designMode === true (Theme Editor / Customizer) the
 *     Storefront API fetch is skipped entirely to avoid CSP sandbox issues and
 *     endless skeleton shimmer. Placeholder labels are shown instead.
 *   - A 5 s fallback timer resolves any remaining skeletons to "—" on the live
 *     site in case the token is missing or the fetch fails silently.
 */

(function () {
  'use strict';

  const section = document.querySelector('[data-comparison-table]');
  if (!section) return;

  const {
    storefrontToken,
    storefrontVersion,
    shopDomain,
    competitor1Gid,
    competitor2Gid,
    locale
  } = section.dataset;

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Show a static text label in all pending skeleton cells WITHOUT removing the
   * data-gid-field attribute. Used for temporary states (e.g., "Loading…") so
   * hydrateCompetitor() can still locate and overwrite the cells afterwards.
   */
  function showLoadingLabel(text) {
    section.querySelectorAll('[data-gid-field]').forEach(cell => {
      if (cell.tagName === 'IMG') return;
      cell.innerHTML = '';
      cell.textContent = text;
    });
  }

  /**
   * Final resolution: replace every remaining skeleton/placeholder with `text`
   * and remove the data-gid-field attribute so the fallback timer cannot
   * overwrite content already hydrated successfully by hydrateCompetitor().
   */
  function resolveSkeletons(text) {
    section.querySelectorAll('[data-gid-field]').forEach(cell => {
      // Header image – hide gracefully
      if (cell.tagName === 'IMG') {
        cell.style.display = 'none';
        return;
      }
      cell.innerHTML = '';
      cell.textContent = text;
      cell.removeAttribute('data-gid-field');
    });
  }

  // ─── Customizer guard ───────────────────────────────────────────────────────
  // In the Theme Editor Shopify sets window.Shopify.designMode = true.
  // We previously skipped the fetch here, but the Storefront API endpoint is
  // the shop's own domain and is accessible from the customizer iframe.
  // We only swap the shimmer animation for a static "Loading…" text so the
  // editor does not show pulsating bars while the fetch completes.
  const isDesignMode = window.Shopify && window.Shopify.designMode;
  if (isDesignMode) {
    showLoadingLabel('Loading…');
    // Do NOT return — continue to fetch and hydrate normally.
  }

  // ─── Token guard ────────────────────────────────────────────────────────────
  if (!storefrontToken) {
    resolveSkeletons('—');
    hideEmptyRows();
    return;
  }

  // ─── Fallback timer ─────────────────────────────────────────────────────────
  // If the fetch takes longer than 5 s or fails silently, stop shimmer pulsing.
  const fallbackTimer = setTimeout(() => {
    resolveSkeletons('—');
    hideEmptyRows();
  }, 5000);

  const endpoint = `https://${shopDomain}/api/${storefrontVersion || '2025-01'}/graphql.json`;

  /**
   * GraphQL query to fetch a metaobject by its GID and return all field key-value pairs.
   * For file/image references the `reference` union is expanded so we get a usable URL.
   */
  const METAOBJECT_QUERY = `
    query GetMetaobject($id: ID!) {
      metaobject(id: $id) {
        fields {
          key
          value
          reference {
            ... on MediaImage {
              image {
                url
              }
            }
            ... on GenericFile {
              url
              previewImage {
                url
              }
            }
          }
        }
      }
    }
  `;

  /**
   * Fetch a single metaobject from the Storefront API
   * @param {string} gid - The metaobject global ID
   * @returns {Promise<object|null>} Key-value map of metaobject fields, or null on error
   */
  async function fetchMetaobject(gid) {
    if (!gid) return null;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken,
          'Accept-Language': locale || 'it'
        },
        body: JSON.stringify({
          query: METAOBJECT_QUERY,
          variables: { id: gid }
        })
      });

      if (!response.ok) return null;

      const json = await response.json();

      if (json.errors || !json?.data?.metaobject) return null;

      // Extract fields and convert to key-value map
      // For image/file fields, prefer the resolved reference URL over the raw GID value
      const fields = json.data.metaobject.fields ?? [];
      const result = Object.fromEntries(fields.map(f => {
        const imageUrl = f.reference?.image?.url
          || f.reference?.url
          || f.reference?.previewImage?.url;
        return [f.key, imageUrl || f.value];
      }));
      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Extract plain text from a Shopify rich text JSON string.
   * Shopify stores rich text fields as {"type":"root","children":[...]} JSON.
   * Falls back to returning the original value if it is not rich text JSON.
   * @param {string} value
   * @returns {string}
   */
  function extractText(value) {
    if (!value || typeof value !== 'string') return value;
    // Only attempt parse if it looks like a JSON object
    if (!value.startsWith('{')) return value;
    try {
      const parsed = JSON.parse(value);
      const collect = (node) => {
        if (!node) return '';
        if (node.type === 'text') return node.value || '';
        if (Array.isArray(node.children)) {
          return node.children.map(collect).join('').replace(/\n+$/, '');
        }
        return '';
      };
      return collect(parsed).trim() || '';
    } catch (e) {
      return value;
    }
  }

  /**
   * Hide any <tbody> row where every data cell (all columns except the
   * feature-label column) contains only the "—" placeholder.  Runs once
   * after hydration + resolveSkeletons so we know final cell content.
   */
  function hideEmptyRows() {
    const rows = section.querySelectorAll('tbody .comparison-table__row');
    rows.forEach(tr => {
      const dataCells = tr.querySelectorAll(
        'td:not(.comparison-table__cell--feature)'
      );
      const allEmpty = Array.from(dataCells).every(td => {
        const text = td.textContent.trim();
        return text === '—' || text === '';
      });
      if (allEmpty) tr.style.display = 'none';
    });
  }

  /**
   * Hydrate competitor cells with resolved metaobject data
   * @param {number} competitorNum - 1 or 2, denoting which competitor column
   * @param {object} data - Key-value map of metaobject fields
   */
  function hydrateCompetitor(competitorNum, data) {
    if (!data) return;

    // Hydrate header section
    const headerTarget = section.querySelector(`[data-hydration-target="${competitorNum}"]`);
    if (headerTarget) {
      // Product name
      const nameEl = headerTarget.querySelector(`[data-gid-field="competitor_product"]`);
      if (nameEl && data.competitor_product) {
        nameEl.textContent = extractText(data.competitor_product);
        nameEl.removeAttribute('data-gid-field');
      }

      // Brand
      const brandEl = headerTarget.querySelector(`[data-gid-field="competitor_brand"]`);
      if (brandEl && data.competitor_brand) {
        brandEl.textContent = extractText(data.competitor_brand);
        brandEl.style.display = 'block';
        brandEl.removeAttribute('data-gid-field');
      }

      // Image — use CDN URL resolved by the GraphQL reference union
      const imgEl = headerTarget.querySelector(`[data-gid-field="competitor_p_img"]`);
      const imgUrl = data.competitor_p_img && data.competitor_p_img.startsWith('http')
        ? data.competitor_p_img
        : null;

      if (imgEl) {
        if (imgUrl) {
          imgEl.src = imgUrl;
          imgEl.style.display = 'block';
        } else {
          imgEl.style.display = 'none';
        }
        imgEl.removeAttribute('data-gid-field');
      }
    }

    // Hydrate table body cells for this competitor
    const bodyCells = section.querySelectorAll(
      `[data-gid-field][data-competitor="${competitorNum}"]`
    );
    bodyCells.forEach(cell => {
      const fieldKey = cell.dataset.gidField;
      const value = data[fieldKey];

      if (value) {
        cell.innerHTML = '';
        cell.textContent = extractText(value);
        // Mark as hydrated so resolveSkeletons() does not overwrite this cell
        cell.removeAttribute('data-gid-field');
      }
    });
  }

  /**
   * Initiate the hydration process by fetching both competitors in parallel
   */
  async function initialize() {
    try {
      const [competitor1Data, competitor2Data] = await Promise.all([
        fetchMetaobject(competitor1Gid),
        fetchMetaobject(competitor2Gid)
      ]);

      // Cancel the fallback timer — we got a response
      clearTimeout(fallbackTimer);

      await Promise.all([
        hydrateCompetitor(1, competitor1Data),
        hydrateCompetitor(2, competitor2Data)
      ]);

      // Resolve any cells that had no value returned (competitor had no data for that field)
      resolveSkeletons('—');
      // Hide rows where all data cells are empty
      hideEmptyRows();
    } catch (err) {
      clearTimeout(fallbackTimer);
      resolveSkeletons('—');
      hideEmptyRows();
    }
  }

  // Run initialization on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
